/*
 * NutriProVita cart — cosmetic coupon, free-shipping bar, item count, and the
 * two-way drawer <-> /cart page sync.
 * Sense's cart.js / cart-drawer.js handle open/close, qty, remove and the live
 * re-render of the surface the user touched. The coupon just captures a code into
 * the Shopify cart note (persisted via <cart-note> -> /cart/update.js); real
 * discounting is at checkout. Delegated on document so it survives section
 * re-renders.
 */
(function () {
  function applyCoupon(fromEl) {
    var wrap = fromEl.closest('[data-npv-coupon]');
    if (!wrap) return;
    var input = wrap.querySelector('[data-npv-coupon-input]');
    var btn = wrap.querySelector('[data-npv-coupon-apply]');
    var code = ((input && input.value) || '').trim().toUpperCase();
    if (!code) return;

    var scope = wrap.closest('cart-drawer') || document;
    var note = scope.querySelector('[data-npv-note]') || document.querySelector('[data-npv-note]');
    if (note) {
      var kept = (note.value || '').split('\n').filter(function (l) {
        return l.trim() && l.indexOf('Coupon:') !== 0;
      });
      kept.push('Coupon: ' + code);
      note.value = kept.join('\n');
      note.dispatchEvent(new Event('input', { bubbles: true }));
    }

    wrap.classList.add('is-applied');
    if (btn) btn.textContent = wrap.dataset.appliedLabel || '✓ Applied';
    var hint = wrap.parentElement && wrap.parentElement.querySelector('[data-npv-coupon-hint]');
    if (hint) hint.hidden = false;
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-npv-coupon-apply]');
    if (btn) {
      e.preventDefault();
      applyCoupon(btn);
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' && e.target.matches && e.target.matches('[data-npv-coupon-input]')) {
      e.preventDefault();
      applyCoupon(e.target);
    }
  });

  /* Free-shipping bar lives outside cart.js's .js-contents re-render region, so
     recompute it here from the cartUpdate pub/sub (cartData.total_price is in
     minor units). Display only. */
  function updateShipBar(totalCents) {
    var bar = document.querySelector('.npv-cart__ship');
    if (!bar || typeof totalCents !== 'number') return;
    var thresholdCents = parseInt(bar.dataset.threshold, 10);
    if (!thresholdCents) return;

    var pct = Math.min((totalCents / thresholdCents) * 100, 100);
    var unlocked = totalCents >= thresholdCents;
    bar.classList.toggle('is-unlocked', unlocked);
    var fill = bar.querySelector('.npv-cart__ship-fill');
    if (fill) fill.style.width = pct + '%';

    // All copy is editable in the theme editor and handed over via data-attributes;
    // tokens: [amount] still to spend, [total] basket total, [threshold] free-shipping amount.
    var fmt = bar.dataset.moneyFormat;
    var thresholdMoney = money(thresholdCents, fmt);
    var spans = bar.querySelectorAll('.npv-cart__ship-top span');
    if (spans[0]) {
      var left = (bar.dataset.msgRemaining || 'Add [amount] more for free shipping')
        .replace('[amount]', money(thresholdCents - totalCents, fmt))
        .replace('[threshold]', thresholdMoney);
      spans[0].textContent = unlocked ? bar.dataset.msgUnlocked || '🎉 You’ve unlocked free shipping!' : left;
    }
    if (spans[1]) {
      spans[1].textContent = (bar.dataset.msgProgress || '[total] / [threshold]')
        .replace('[total]', money(totalCents, fmt))
        .replace('[threshold]', thresholdMoney);
    }
  }

  // Format minor units with the shop's own money format ("£{{amount}}", "{{amount_no_decimals}} €" …);
  // the server render is authoritative on load.
  function money(cents, format) {
    var amount = (cents / 100).toFixed(2);
    var whole = String(Math.round(cents / 100));
    var f = format || '£{{amount}}';
    return f
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, whole)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, whole)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','))
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{[^}]*\}\}/, amount);
  }

  /* ── Two-way sync: side cart drawer <-> /cart page ────────────────────────
     Both surfaces are Sense `CartItems` elements. Sense only re-renders the
     surface the user touched — a `cart-items` cartUpdate is ignored by every
     other CartItems — and its own cross-refresh (CartItems.onCartUpdate) looks
     for markup this theme doesn't have (`.cart-drawer__footer`, a
     `main-cart-items` section id). So that method is neutered here and these
     two refreshers do the job from the Section Rendering API:
       - change made on the PAGE   -> silently re-render the drawer
       - change made in the DRAWER -> silently re-render the page's list + totals
       - add-to-cart (product-form / quick-add) -> product-form.js already renders
         + opens the drawer; the page (if we're on /cart) is refreshed here.
     `origin` records which surface the user last interacted with, because the
     cartUpdate payload is `source: 'cart-items'` for both. */
  var origin = null;
  ['click', 'change', 'input', 'keydown', 'pointerdown'].forEach(function (ev) {
    document.addEventListener(
      ev,
      function (e) {
        var t = e.target;
        if (!t || !t.closest) return;
        if (t.closest('cart-drawer')) origin = 'drawer';
        else if (t.closest('cart-items')) origin = 'page';
      },
      true
    );
  });

  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('cart-items').then(function () {
      var Ctor = customElements.get('cart-items');
      if (Ctor && Ctor.prototype) {
        Ctor.prototype.onCartUpdate = function () {
          return Promise.resolve();
        };
      }
    });
  }

  function parse(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  var drawerSeq = 0;
  function refreshDrawer(data) {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return Promise.resolve();
    var seq = ++drawerSeq;
    var rootUrl = (window.routes && window.routes.root_url) || '/';
    return fetch(rootUrl + '?section_id=cart-drawer')
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        if (seq !== drawerSeq) return; // a newer refresh superseded this one
        var doc = parse(html);
        var fresh = doc.querySelector('#CartDrawer');
        var holder = drawer.querySelector('#CartDrawer');
        if (!fresh || !holder) return;
        var body = holder.querySelector('.npv-drawer__body');
        var scroll = body ? body.scrollTop : 0;
        holder.innerHTML = fresh.innerHTML;
        // renderContents() re-adds this after swapping #CartDrawer's innerHTML too
        var overlay = holder.querySelector('#CartDrawer-Overlay');
        if (overlay && typeof drawer.close === 'function') overlay.addEventListener('click', drawer.close.bind(drawer));
        var freshRoot = doc.querySelector('cart-drawer');
        var empty = freshRoot ? freshRoot.classList.contains('is-empty') : data && data.item_count === 0;
        drawer.classList.toggle('is-empty', !!empty);
        var newBody = holder.querySelector('.npv-drawer__body');
        if (newBody) newBody.scrollTop = scroll;
      })
      .catch(function (e) {
        console.error(e);
      });
  }

  var pageSeq = 0;
  function refreshPage() {
    var itemsWrap = document.getElementById('main-cart-items');
    var footWrap = document.getElementById('main-cart-footer');
    if (!itemsWrap) return Promise.resolve();
    var ids = [itemsWrap.dataset.id];
    if (footWrap && footWrap.dataset.id) ids.push(footWrap.dataset.id);
    var seq = ++pageSeq;
    return fetch(window.location.pathname + '?sections=' + ids.join(','))
      .then(function (r) {
        return r.json();
      })
      .then(function (sections) {
        if (seq !== pageSeq) return;
        var itemsHtml = sections[itemsWrap.dataset.id];
        if (itemsHtml) {
          var itemsDoc = parse(itemsHtml);
          var list = itemsWrap.querySelector('.js-contents');
          var freshList = itemsDoc.querySelector('.js-contents');
          if (list && freshList) list.innerHTML = freshList.innerHTML;
          var cartItems = itemsWrap.closest('cart-items');
          var freshCI = itemsDoc.querySelector('cart-items');
          if (cartItems && freshCI) cartItems.classList.toggle('is-empty', freshCI.classList.contains('is-empty'));
        }
        var footHtml = footWrap && footWrap.dataset.id && sections[footWrap.dataset.id];
        if (footHtml) {
          var footDoc = parse(footHtml);
          var fl = footWrap.querySelector('.js-contents');
          var ffl = footDoc.querySelector('.js-contents');
          if (fl && ffl) fl.innerHTML = ffl.innerHTML;
          var ff = footDoc.querySelector('#main-cart-footer');
          if (ff) footWrap.classList.toggle('is-empty', ff.classList.contains('is-empty'));
        }
      })
      .catch(function (e) {
        console.error(e);
      });
  }

  function updateCount(data) {
    var el = document.querySelector('.npv-cart__count');
    if (!el || !data || typeof data.item_count !== 'number') return;
    var one = el.dataset.singular || 'item';
    var many = el.dataset.plural || 'items';
    el.textContent = '(' + data.item_count + ' ' + (data.item_count === 1 ? one : many) + ')';
  }

  // constants.js / pubsub.js expose PUB_SUB_EVENTS + subscribe as classic-script
  // globals (lexical, not on window), same as cart.js uses them.
  try {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        var data = event && event.cartData;
        if (data && typeof data.total_price === 'number') updateShipBar(data.total_price);
        updateCount(data);

        var src = event && event.source;
        if (src === 'cart-items') {
          if (origin === 'page') refreshDrawer(data);
          else if (origin === 'drawer') refreshPage();
        } else if (src === 'product-form' || src === 'quick-add') {
          refreshPage(); // the drawer is rendered + opened by product-form.js itself
        }
      });
    }
  } catch (e) {
    /* pub/sub not available — surfaces just update on full page load */
  }
})();
